/**
 * Property map: MapLibre + theme style JSON on page load.
 *
 * Style/tiles: window.europarcsMap.styleUrl (OpenFreeMap sources).
 */

// No remote fallback on purpose: the theme route is the only style whose
// attribution string is ours rather than a third party's.
const MAP_STYLE_URL = (window.europarcsMap && window.europarcsMap.styleUrl) || '';

const MAP_GL_JS = (window.europarcsMap && window.europarcsMap.glJs) || '';

const MAP_GL_CSS = (window.europarcsMap && window.europarcsMap.glCss) || '';

let mapGlLoader = null;

function mapGlNamespace() {
  if (typeof maplibregl !== 'undefined') {
    return maplibregl;
  }
  return null;
}

/**
 * MapLibre 4 starts compact attribution expanded (copyright + "i").
 * Collapse to the "i" once attributions have been written; leave later toggles alone.
 */
function collapseMapAttribution(container) {
  if (!container) {
    return false;
  }

  const attrib = container.querySelector('.maplibregl-ctrl-attrib');
  if (!attrib) {
    return false;
  }

  if (attrib.classList.contains('maplibregl-attrib-empty')) {
    return false;
  }

  const inner = attrib.querySelector('.maplibregl-ctrl-attrib-inner');
  if (!inner || !inner.innerHTML.trim()) {
    return false;
  }

  attrib.classList.remove('maplibregl-compact-show');
  attrib.removeAttribute('open');
  return true;
}

/**
 * Load MapLibre GL JS + CSS once. Safe to call from map-search and property maps.
 */
function loadMapGl() {
  if (mapGlNamespace()) {
    return Promise.resolve();
  }

  if (mapGlLoader) {
    return mapGlLoader;
  }

  mapGlLoader = new Promise(function (resolve, reject) {
    if (!MAP_GL_JS || !MAP_GL_CSS) {
      reject(new Error('MapLibre asset URLs missing (europarcsMap.glJs / glCss)'));
      return;
    }

    if (!document.querySelector('link[data-europarcs-map-css]')) {
      const css = document.createElement('link');
      css.rel = 'stylesheet';
      css.href = MAP_GL_CSS;
      css.setAttribute('data-europarcs-map-css', '1');
      document.head.appendChild(css);
    }

    const existing = document.querySelector('script[data-europarcs-map-js]');
    if (existing) {
      if (mapGlNamespace()) {
        resolve();
        return;
      }
      existing.addEventListener('load', function () {
        resolve();
      });
      existing.addEventListener('error', reject);
      setTimeout(function () {
        if (mapGlNamespace()) {
          resolve();
        } else {
          reject(new Error('MapLibre GL load timeout'));
        }
      }, 8000);
      return;
    }

    const script = document.createElement('script');
    script.src = MAP_GL_JS;
    script.async = true;
    script.setAttribute('data-europarcs-map-js', '1');
    script.onload = function () {
      resolve();
    };
    script.onerror = reject;
    document.head.appendChild(script);
  }).then(
    function (value) {
      return value;
    },
    function (err) {
      mapGlLoader = null;
      throw err;
    }
  );

  return mapGlLoader;
}

window.europarcsLoadMapLibre = loadMapGl;
window.europarcsCollapseMapAttribution = collapseMapAttribution;

const MAP_ERROR_MESSAGE =
  (window.europarcsMap && window.europarcsMap.i18n && window.europarcsMap.i18n.mapError) ||
  'Kaart kon niet worden geladen. Controleer uw internetverbinding.';

/**
 * How far to zoom for a given coordinate provenance.
 *
 * A park centroid is the whole park rather than the property, so zooming to
 * street level there would point confidently at the wrong building.
 */
const ZOOM_BY_CONFIDENCE = {
  ok_exact: 15,
  ok_approximate: 13,
  park: 11,
};
const DEFAULT_ZOOM = 12;

function showMapError(mapElement) {
  if (mapElement.dataset.mapError === 'true') {
    return;
  }
  mapElement.dataset.mapError = 'true';

  mapElement.textContent = '';
  const box = document.createElement('div');
  box.className = 'map-error';
  box.textContent = MAP_ERROR_MESSAGE;
  mapElement.appendChild(box);
}

/**
 * Initialize a property map
 * @param {string} mapId - The ID of the map container element
 */
function initPropertyMap(mapId) {
  const mapElement = document.getElementById(mapId);

  if (!mapElement) {
    console.error('Map element not found:', mapId);
    return;
  }

  if (mapElement.dataset.mapBound === 'true') {
    return;
  }
  mapElement.dataset.mapBound = 'true';

  const latitude = parseFloat(mapElement.dataset.latitude);
  const longitude = parseFloat(mapElement.dataset.longitude);

  if (isNaN(latitude) || isNaN(longitude)) {
    console.error('Invalid coordinates:', latitude, longitude);
    return;
  }

  loadMapGl()
    .then(function () {
      setupMap(mapId, mapElement, latitude, longitude);
    })
    .catch(function (err) {
      console.error('MapLibre GL failed to load', err);
      showMapError(mapElement);
    });
}

function setupMap(mapId, mapElement, latitude, longitude) {
  const gl = mapGlNamespace();
  if (!gl) {
    console.error('MapLibre GL JS is not loaded');
    showMapError(mapElement);
    return;
  }

  if (!MAP_STYLE_URL) {
    console.error('Missing europarcsMap.styleUrl');
    showMapError(mapElement);
    return;
  }

  const title = mapElement.dataset.title || 'Location';
  const description = mapElement.dataset.description || '';
  const address = mapElement.dataset.address || '';
  const confidence = mapElement.dataset.confidence || '';
  const zoom = ZOOM_BY_CONFIDENCE[confidence] || DEFAULT_ZOOM;

  let map;
  try {
    map = new gl.Map({
      container: mapId,
      style: MAP_STYLE_URL,
      center: [longitude, latitude],
      zoom: zoom,
      attributionControl: false,
      // Scroll only zooms with a modifier key, and touch needs two fingers, so
      // the map can never trap a scrolling visitor.
      cooperativeGestures: true,
    });
  } catch (err) {
    // Most likely no WebGL: a headless browser, a very old device, or a
    // hardware-acceleration policy.
    console.error('MapLibre GL could not create a map', err);
    showMapError(mapElement);
    return;
  }

  let styleLoaded = false;

  const nav = new gl.NavigationControl({
    showCompass: false,
    showZoom: true,
    visualizePitch: false,
  });
  map.addControl(nav, 'top-right');
  map.addControl(new gl.AttributionControl({ compact: true }), 'bottom-right');

  // Give up after a few idles: an attribution that stays empty would otherwise
  // keep this handler running for the lifetime of the map.
  let collapseAttempts = 0;
  function collapseWhenReady() {
    collapseAttempts += 1;
    if (collapseMapAttribution(mapElement) || collapseAttempts >= 5) {
      map.off('idle', collapseWhenReady);
    }
  }
  map.on('idle', collapseWhenReady);

  map.on('load', function () {
    styleLoaded = true;

    const markerElement = document.createElement('div');
    markerElement.className = 'map-marker';
    markerElement.style.width = '40px';
    markerElement.style.height = '40px';
    markerElement.innerHTML = `
            <div class="map-marker-pin">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="#ef7d00"/>
                </svg>
            </div>
        `;

    const marker = new gl.Marker({ element: markerElement })
      .setLngLat([longitude, latitude])
      .addTo(map);

    const popupNode = document.createElement('div');
    popupNode.className = 'map-popup';
    const titleEl = document.createElement('h4');
    titleEl.className = 'map-popup-title';
    titleEl.textContent = title;
    popupNode.appendChild(titleEl);
    if (address) {
      const addressEl = document.createElement('p');
      addressEl.className = 'map-popup-address';
      addressEl.textContent = address;
      popupNode.appendChild(addressEl);
    }
    if (description) {
      const descriptionEl = document.createElement('p');
      descriptionEl.className = 'map-popup-description';
      descriptionEl.textContent = description;
      popupNode.appendChild(descriptionEl);
    }

    const popup = new gl.Popup({
      offset: 25,
      closeButton: false,
      closeOnClick: false,
    }).setDOMContent(popupNode);

    marker.setPopup(popup);
  });

  map.on('error', function (e) {
    const detail = (e && e.error) || e;

    // Once the style is up, errors are individual tiles, sprites or glyphs
    // failing. Replacing a working map with an error box over one missing tile
    // is worse than the missing tile.
    if (styleLoaded) {
      console.warn('Map resource failed to load:', detail);
      return;
    }

    console.error('Map failed to initialize:', detail);
    showMapError(mapElement);
  });
}

window.initPropertyMap = initPropertyMap;

/**
 * Bind property maps, deferring each until it is close to the viewport.
 *
 * The property map sits well below the fold, so MapLibre (a large library) and
 * its tiles should not compete with the initial render.
 */
function bindPropertyMaps() {
  const elements = Array.prototype.slice.call(document.querySelectorAll('.map_map[data-latitude]'));

  const targets = elements.filter(function (el) {
    return el.id;
  });

  if (!targets.length) {
    return;
  }

  if (typeof IntersectionObserver === 'undefined') {
    targets.forEach(function (el) {
      initPropertyMap(el.id);
    });
    return;
  }

  const observer = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) {
          return;
        }
        observer.unobserve(entry.target);
        initPropertyMap(entry.target.id);
      });
    },
    { rootMargin: '200px' }
  );

  targets.forEach(function (el) {
    observer.observe(el);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bindPropertyMaps);
} else {
  bindPropertyMaps();
}
